import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Wifi,
  Phone,
  Tv,
  CheckCircle,
  Block,
  Cancel,
  Schedule,
  Edit,
  Done,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { Contract, ContratoServicos } from '../../types';
import contractService from '../../services/contractService';
import TabbedContractForm from '../forms/TabbedContractForm';
import { getStatusColor, getStatusLabel, getStatusIcon } from '../../utils/contractStatusUtils';
import { usePermissions } from '../../contexts/AuthContext';
import { tableHeadRowSx, themedTableContainerSx } from '../../styles/theme';

interface ClientContractsProps {
  clientId: number;
}

const ClientContracts: React.FC<ClientContractsProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { can } = usePermissions();
  const podeCriar = can('contratos.criar');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showCancelados, setShowCancelados] = useState(false);

  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const canceladosCount = useMemo(() => contracts.filter(c => c.status === 'cancelado').length, [contracts]);
  const filteredContracts = useMemo(() => showCancelados ? contracts : contracts.filter(c => c.status !== 'cancelado'), [contracts, showCancelados]);

  useEffect(() => {
    loadContracts();
  }, [clientId, page, rowsPerPage]);

  const loadContracts = async () => {
    try {
      setLoading(true);

      // Chamar serviço real de contratos
      const response = await contractService.getByClientId(clientId, {
        page: page + 1, // API usa 1-indexed, mas MUI usa 0-indexed
        limit: rowsPerPage
      });

      if (response.success && response.data) {
        setContracts(response.data);
        setTotalCount(response.pagination?.total || 0);
      } else {
        toast.error('Erro ao carregar contratos');
        setContracts([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('Erro ao carregar contratos:', err);
      toast.error('Erro ao carregar contratos');
      setContracts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleViewContract = (contractId: number) => {
    navigate(`/contracts/${contractId}`);
  };

  const onSubmit = async (data: Partial<Contract>) => {
    try {
      setLoading(true);

      // Preparar dados para envio
      const contractData = {
        ...data,
        cliente_id: clientId,
      };

      const response = await contractService.create(contractData as any);

      if (response.success) {
        handleCloseDialog();
        loadContracts();
        toast.success('Contrato criado com sucesso!');
      } else {
        // Tratar erros específicos de unicidade
        if (response.errors && Array.isArray(response.errors)) {
          // Verificar se são erros de unicidade estruturados
          const uniqueErrors = response.errors.filter((error: any) =>
            error.type === 'unique_violation' && error.field && error.message
          );

          if (uniqueErrors.length > 0) {
            // Mostrar mensagens específicas para cada campo via toast
            uniqueErrors.forEach((error: any) => {
              toast.error(error.message);
            });
          } else {
            // Erros estruturados mas não de unicidade
            const errorMessages = response.errors
              .map((error: any) => error.message || error);
            errorMessages.forEach((message: string) => {
              toast.error(message);
            });
          }
        } else {
          toast.error(response.message || 'Erro ao salvar contrato');
        }
      }
    } catch (err: any) {
      console.error('Erro ao salvar contrato:', err);

      // Tratar erros da requisição HTTP
      if (err.response?.data) {
        const errorData = err.response.data;

        // Verificar se são erros de unicidade estruturados
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const uniqueErrors = errorData.errors.filter((error: any) =>
            error.type === 'unique_violation' && error.field && error.message
          );

          if (uniqueErrors.length > 0) {
            // Mostrar mensagens específicas para cada campo via toast
            uniqueErrors.forEach((error: any) => {
              toast.error(error.message);
            });
          } else {
            // Outros tipos de erro estruturado
            const errorMessages = errorData.errors
              .map((error: any) => error.message || error);
            errorMessages.forEach((message: string) => {
              toast.error(message);
            });
          }
        } else {
          toast.error(errorData.message || 'Erro ao salvar contrato');
        }
      } else {
        toast.error('Erro ao salvar contrato');
      }
    } finally {
      setLoading(false);
    }
  };



  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Resetar para primeira página
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'internet':
        return <Wifi fontSize="small" />;
      case 'telefonia':
        return <Phone fontSize="small" />;
      case 'tv':
        return <Tv fontSize="small" />;
      default:
        return null;
    }
  };

  const renderServicos = (servicos: ContratoServicos[] = []) => {
    if (servicos.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          Nenhum serviço
        </Typography>
      );
    }

    return (
      <Box>
        {servicos.map((servico, index) => (
          <Box key={servico.id} display="flex" alignItems="center" gap={0.5} mb={0.5}>
            {getTipoIcon(servico.tipo)}
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
              {servico.tipo}
            </Typography>
            {servico.plano && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                - {servico.plano.nome}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              - {servico.usuario_pppoe}
            </Typography>
            <Box display="flex" flexDirection="column" ml={1}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" textAlign="right" color="text.secondary" sx={{ fontSize: '0.63rem', minWidth: 58 }}>
                  Status:
                </Typography>
                <Chip
                  label={getStatusLabel(servico.status)}
                  color={getStatusColor(servico.status) as any}
                  size="small"
                  sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
                />
                {servico.isento && (
                  <Chip
                    label="Isento"
                    color="warning"
                    size="small"
                    variant="outlined"
                    sx={{ height: 16, fontSize: '0.75rem', ml: 0.5 }}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" textAlign="right" color="text.secondary" sx={{ fontSize: '0.63rem', minWidth: 58 }}>
                  Conexão:
                </Typography>
                {servico.tipo === 'internet' && servico.usuario_pppoe && (
                  <Chip
                    label={servico.conexao === 'Online' ? 'Online' : 'Offline'}
                    color={servico.conexao === 'Online' ? 'success' : 'error'}
                    size="small"
                    sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
            Contratos do Cliente
          </Typography>
          {canceladosCount > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCancelados}
                  onChange={(e) => setShowCancelados(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Exibir cancelados ({canceladosCount})
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          )}
        </Box>
        <Box display="flex" gap={1}>
          {podeCriar && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            disabled={loading}
            size="small"
            sx={{ display: { xs: 'none', sm: 'flex' }, fontSize: '0.8rem', px: 1.5, py: 0.5 }}
          >
            Novo Contrato
          </Button>
          )}
        </Box>
      </Box>

      {/* Tabela de Contratos */}
      <TableContainer component={Paper} elevation={1} sx={themedTableContainerSx(theme)}>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadRowSx(theme)}>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Num.</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Título</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Serviços</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', py: 0.75, fontSize: '0.8rem' }}>Valor Contratado</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Endereço</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Assinado</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
                    Carregando contratos...
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && filteredContracts.map((contract) => (
              <TableRow key={contract.id} hover>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>#{contract.id}</TableCell>
                <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1.5 }} onClick={() => handleViewContract(contract.id)}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                      {contract.titulo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {formatDate(contract.data_contrato)}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap', cursor: 'pointer', py: 0.5, px: 1.5 }} onClick={() => handleViewContract(contract.id)}>
                  {renderServicos(contract.servicos_adicionais)}
                </TableCell>

                <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1.5 }} onClick={() => handleViewContract(contract.id)}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatCurrency(contract.valor_contratado)}</Typography>
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap', cursor: 'pointer', py: 0.5, px: 1.5 }} onClick={() => handleViewContract(contract.id)}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{contract.endereco_inst} - {contract.numero_inst}</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{contract.bairro_inst} - {contract.cidade_inst} - {contract.estado_inst}</Typography>
                </TableCell>

                <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1.5 }} onClick={() => handleViewContract(contract.id)}>
                  {contract.data_assinatura ? (
                    <Tooltip title={`Assinado em ${new Date(contract.data_assinatura).toLocaleString('pt-BR')}${contract.assinado_por ? ` por ${contract.assinado_por}` : ''}`}>
                      <Chip
                        icon={<Done fontSize="small" />}
                        label="Assinado"
                        color="success"
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Contrato ainda não assinado">
                      <Chip
                        icon={<Edit fontSize="small" />}
                        label="Não assinado"
                        color="default"
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1.5 }} onClick={() => handleViewContract(contract.id)}>
                  <Chip
                    label={getStatusLabel(contract.status)}
                    color={getStatusColor(contract.status) as any}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </TableCell>
              </TableRow>
            ))}

            {!loading && filteredContracts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    Nenhum contrato encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Paginação */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
          sx={{
            '& .MuiTablePagination-toolbar': {
              fontSize: '0.8rem'
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem'
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: '0.8rem'
            }
          }}
        />
      </TableContainer>

      {/* FAB para mobile */}
      {isMobile && podeCriar && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 86, // Footer height (70px) + spacing (16px) = 86px
            right: 16,
            zIndex: theme.zIndex.appBar - 1, // Abaixo do FAB do dashboard mas acima do footer
          }}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          <Add />
        </Fab>
      )}

      {/* Formulário de Contrato com Abas */}
      <TabbedContractForm
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
        clientId={clientId}
        loading={loading}
      />
    </Box>
  );
};

export default ClientContracts; 